import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";

import { ConfirmationModalVariant } from "../../../ConfirmationModal";
import { getEntitySingularLabel } from "../../entity-labels";
import { getItemColor } from "../../utils";

import { ENDPOINTS } from "@/api/application-api";
import { EntityIcon } from "@/components/ui/icons";
import { useAuth } from "@/context/auth-context";
import { useConfirm } from "@/context/confirm-context";
import { useUser } from "@/context/user-context";
import { ToastType } from "@/enums";
import { useFolders } from "@/queries";
import { Entity, EntityType, getRequiredEntityNumericId } from "@/queries/folder-entities/types";
import { useDeleteFolderEntities } from "@/queries/folder-entities/useDeleteFolderEntities";
import { useDeleteSkillCollection } from "@/queries/skill-collections";
import { Folder } from "@/types/folders";
import { jsonAuthHeaders } from "@/utils/headers";
import { displayToast } from "@/utils/toast";

const getEntityTypeLabelCapitalized = (entity: Entity) => getEntitySingularLabel(entity.type);

const getWarningMessage = (entity: Entity, isArchive?: boolean) => {
  if (isArchive) {
    switch (entity.type) {
      case "FOLDER":
        return "This folder and all its contents will be archived. You can restore it later if needed.";
      case "PROMPT":
        return "This prompt template and all its versions will be archived. You can restore it later if needed.";
      case "WORKFLOW":
        return "This workflow and all its data will be archived. You can restore it later if needed.";
      case "DATASET":
        return "This dataset and all its data will be archived and hidden from dataset lists.";
      case "REPORT":
        return "This evaluation and all its results will be archived. You can restore it later if needed.";
      case "AB_TEST":
        return "This A/B test and all its data will be archived. You can restore it later if needed.";
      case "SKILL_COLLECTION":
        return "This skill collection and all its files will be archived. You can restore it later if needed.";
      case "TOOL":
        return "This tool will be archived. Any prompts referencing this tool will be affected.";
      default:
        return "This item will be archived. You can restore it later if needed.";
    }
  }

  switch (entity.type) {
    case "FOLDER":
      return "This folder and all its contents will be permanently deleted. This action cannot be undone.";
    case "PROMPT":
      return "This prompt template and all its versions will be permanently deleted. This action cannot be undone.";
    case "WORKFLOW":
      return "This workflow and all its data will be permanently deleted. This action cannot be undone.";
    case "DATASET":
      return "This dataset and all its data will be permanently deleted. This action cannot be undone.";
    case "REPORT":
      return "This evaluation and all its results will be permanently deleted. This action cannot be undone.";
    case "AB_TEST":
      return "This A/B test and all its data will be permanently deleted. This action cannot be undone.";
    case "SKILL_COLLECTION":
      return "This skill collection and all its files will be permanently deleted. This action cannot be undone.";
    case "TOOL":
      return "This tool and all its versions will be permanently deleted. Any prompts referencing this tool will be affected. This action cannot be undone.";
    default:
      return "This item will be permanently deleted. This action cannot be undone.";
  }
};

export const useDeleteEntity = () => {
  const { confirm } = useConfirm();
  const authContext = useAuth();
  const userContext = useUser();
  const { userToken } = authContext!;
  const { workspaceId, folderId } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const deleteEntitiesMutation = useDeleteFolderEntities(userToken!);
  const deleteSkillCollectionMutation = useDeleteSkillCollection();
  const foldersQuery = useFolders(userToken!, userContext.activeWorkspaceId!);

  const invalidateQueries = () => {
    // Invalidate the current folder's entities
    queryClient.invalidateQueries({
      queryKey: ["folder-entities", workspaceId, folderId ? Number(folderId) : undefined],
    });

    queryClient.invalidateQueries({
      queryKey: ["folder-entities", workspaceId, undefined, undefined, undefined, true],
    });

    // Also invalidate parent folder if we're in a subfolder
    if (folderId) {
      queryClient.invalidateQueries({
        queryKey: ["folder-entities", workspaceId, undefined],
      });
    }
  };

  const invalidateSkillCollections = () => {
    if (!workspaceId) {
      return;
    }

    queryClient.invalidateQueries({
      queryKey: ["skill-collections", Number(workspaceId)],
    });
  };

  const deleteEntities = async (entities: Entity[]) => {
    if (entities.length === 0) {
      return;
    }

    await deleteEntitiesMutation.mutateAsync({
      entities: entities.map(entity => ({
        id: entity.id,
        type: entity.type === EntityType.SNIPPET ? EntityType.PROMPT : entity.type,
      })),
    });
  };

  const deleteSkillCollections = async (entities: Entity[]) => {
    if (!workspaceId || entities.length === 0) {
      return;
    }

    await Promise.all(
      entities.map(entity =>
        deleteSkillCollectionMutation.mutateAsync({
          workspaceId: Number(workspaceId),
          collectionId: entity.id,
        }),
      ),
    );
  };

  const deleteEntity = async (entity: Entity, onSuccess?: () => void, isArchive?: boolean) => {
    if (!entity) {
      displayToast("Invalid item selected", ToastType.error);
      return;
    }

    const entityTypeLabelCapitalized = getEntityTypeLabelCapitalized(entity);
    const actionLabel = isArchive ? "Archive" : "Delete";
    const icon = <EntityIcon type={entity.type} size="md" />;
    const iconBgColor = getItemColor(entity.type);

    // For tools, fetch references to show affected prompts
    let warningMessage: React.ReactNode = getWarningMessage(entity, isArchive);
    if (entity.type === EntityType.TOOL && !isArchive && workspaceId) {
      try {
        const res = await fetch(
          ENDPOINTS.tool_registry.references(
            Number(workspaceId),
            getRequiredEntityNumericId(entity.id),
          ),
          { headers: jsonAuthHeaders(userToken!) },
        );
        const data = await res.json();
        const refs = data?.references ?? [];
        if (refs.length > 0) {
          warningMessage = (
            <div className="space-y-2">
              <p>
                This tool is referenced by {refs.length} prompt{refs.length !== 1 ? "s" : ""}:
              </p>
              <ul className="max-h-32 list-disc overflow-auto pl-5 text-sm text-gray-600">
                {refs.map((ref: { prompt_name: string; prompt_id: number }) => (
                  <li key={ref.prompt_id}>{ref.prompt_name}</li>
                ))}
              </ul>
              <p>
                Deleting this tool will remove it from all listed prompts. This action cannot be
                undone.
              </p>
            </div>
          );
        }
      } catch {
        // Silently fall back to static message
      }
    }

    const confirmed = await confirm({
      title: (
        <div className="flex w-full items-center gap-3">
          <div
            className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${iconBgColor}`}
          >
            {icon}
          </div>
          <div className="min-w-0 flex-1 overflow-hidden">
            <span
              className="block truncate"
              title={`${actionLabel} ${entityTypeLabelCapitalized}: "${entity.name}"`}
            >
              {actionLabel} {entityTypeLabelCapitalized}: "{entity.name}"
            </span>
          </div>
        </div>
      ),
      message: warningMessage as string,
      confirmText: `${actionLabel} ${entityTypeLabelCapitalized}`,
      cancelText: "Cancel",
      headerVariant: isArchive ? undefined : ConfirmationModalVariant.destructive,
      saveButtonVariant: isArchive ? "default" : "destructive",
      exitButtonVariant: "outline",
      requireConfirmation: !isArchive,
      confirmationText: entity.name,
      confirmationPlaceholder: entity.name,
    });

    if (!confirmed) {
      return;
    }

    try {
      // Check if we're deleting the current folder
      const isCurrentFolder =
        entity.type === "FOLDER" && folderId && entity.id === Number(folderId);

      if (entity.type === EntityType.SKILL_COLLECTION) {
        await deleteSkillCollections([entity]);
        invalidateSkillCollections();
      } else {
        await deleteEntities([entity]);
      }

      displayToast(
        `${entityTypeLabelCapitalized} ${isArchive ? "archived" : "deleted"} successfully`,
        ToastType.success,
      );

      // If we deleted the current folder, redirect to its parent
      if (isCurrentFolder) {
        const folders: Folder[] = foldersQuery.data?.folders || [];
        const currentFolder = folders.find(f => f.id === Number(folderId));
        const parentFolderId = currentFolder?.parent_id;

        if (parentFolderId && parentFolderId !== null) {
          navigate(`/workspace/${workspaceId}/folder/${parentFolderId}`);
        } else {
          navigate(`/workspace/${workspaceId}`);
        }
      }

      invalidateQueries();

      // When a tool is deleted, invalidate tool-registry caches
      if (entity.type === EntityType.TOOL) {
        queryClient.invalidateQueries({ queryKey: ["tool-registry"] });
      }

      onSuccess?.();
    } catch {
      // Error handling is already done by the mutation's onError callback
    }
  };

  const deleteBatch = async (entities: Entity[], onSuccess?: () => void) => {
    if (!entities || entities.length === 0) {
      displayToast("No items selected", ToastType.error);
      return;
    }

    const itemsText = entities.length === 1 ? `"${entities[0].name}"` : `${entities.length} items`;

    const icon = entities.length === 1 ? <EntityIcon type={entities[0].type} size="md" /> : null;
    const iconBgColor = entities.length === 1 ? getItemColor(entities[0].type) : null;

    const confirmed = await confirm({
      title:
        entities.length === 1 && icon ? (
          <div className="flex w-full items-center gap-3">
            <div
              className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${iconBgColor}`}
            >
              {icon}
            </div>
            <div className="min-w-0 flex-1 overflow-hidden">
              <span className="block truncate" title={`Delete ${itemsText}`}>
                Delete {itemsText}
              </span>
            </div>
          </div>
        ) : (
          `Delete ${itemsText}`
        ),
      message: "These items will be permanently deleted. This action cannot be undone.",
      confirmText: `Delete ${itemsText}`,
      cancelText: "Cancel",
      headerVariant: ConfirmationModalVariant.destructive,
      saveButtonVariant: "destructive",
      exitButtonVariant: "outline",
      requireConfirmation: !!entities.length,
      confirmationText: entities.length > 1 ? `Yes, I'm sure` : entities[0].name,
      confirmationPlaceholder: entities.length > 1 ? `Yes, I'm sure` : entities[0].name,
    });

    if (!confirmed) {
      return;
    }

    try {
      // Check if we're deleting the current folder in batch
      const currentFolderBeingDeleted =
        folderId &&
        entities.some(entity => entity.type === "FOLDER" && entity.id === Number(folderId));
      const skillCollections = entities.filter(
        entity => entity.type === EntityType.SKILL_COLLECTION,
      );
      const standardEntities = entities.filter(
        entity => entity.type !== EntityType.SKILL_COLLECTION,
      );

      await Promise.all([
        deleteEntities(standardEntities),
        deleteSkillCollections(skillCollections),
      ]);

      displayToast(`${itemsText} deleted successfully`, ToastType.success);

      // If we deleted the current folder in batch, redirect to its parent
      if (currentFolderBeingDeleted) {
        const folders: Folder[] = foldersQuery.data?.folders || [];
        const currentFolder = folders.find(f => f.id === Number(folderId));
        const parentFolderId = currentFolder?.parent_id;

        if (parentFolderId && parentFolderId !== null) {
          navigate(`/workspace/${workspaceId}/folder/${parentFolderId}`);
        } else {
          navigate(`/workspace/${workspaceId}`);
        }
      }

      invalidateQueries();
      if (skillCollections.length > 0) {
        invalidateSkillCollections();
      }
      onSuccess?.();
    } catch {
      // Error handling is already done by the mutation's onError callback
    }
  };

  return { deleteEntity, deleteBatch };
};
